<template>
  <div class="companions-data__form companions-data__form-update">
    <form v-if="transformedFields.length" class="companions-data__form-fields">
      <div class="form-container">
        <FormItem
          v-for="item in transformedFields"
          :key="item.id"
          :item="item"
          :backend="backend"
          :form-data="companionGuestForm"
          @input="fieldsUpdated = true"
        />
      </div>

      <div v-if="fieldsUpdated" class="companions-data__form-footer">
        <button
          type="button"
          class="companions-data__form-ticket-btn-next btn col-span-full!"
          @click="updateCompanionGuest(companionGuestForm)"
        >
          {{ $t('landingpage.checkout-page.companions.update') }}
        </button>
      </div>
    </form>

    <SelectTicket
      :key="`companion-ticket-${companionGuestForm.ticket}`"
      :value="companionGuestForm.ticket"
      :tickets="tickets"
      :add-ons="addOns"
      is-update
      :assign-add-on="assignAddOnToCompanion"
      @ticket-selected="assignTicketToCompanion"
      @add-on-selected="assignAddOnToCompanion"
      @add-on-removed="removeAddOnFromCompanion"
    >
      <div class="companions-data__form-header-intro">
        <p>{{ $t('landingpage.checkout-page.confirmation.ticket') }}</p>
      </div>
    </SelectTicket>
  </div>
</template>

<script setup>
import { ref, inject, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import SelectTicket from '@/Components/LandingPage/Checkout/SelectTicket.vue'
import FormItem from '@landingpage/Registration/components/FormItem.vue'
import {
  postJson,
  getJson,
  putJson,
  useFetch,
} from '@/LandingPage/Checkout/useCheckoutFetch'
import { getNested, dataGet, dataSet } from '@landingpage/Common/utils'
import { useCheckoutBackend } from '@landingpage/Registration/composables/useBackendAdapter'
import { useFieldPathResolver } from '@landingpage/Registration/composables/useFieldPathResolver'
import { useVisibilityRules } from '@landingpage/Registration/composables/useVisibilityRules'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  fields: { type: [Array, Object], default: () => ({}) },
  model: { type: Object, default: () => ({}) },
})

const emit = defineEmits([
  'close',
  'error',
  'success',
  'tickets-addons-success',
  'is-loading',
  'expired-session',
])

const { getFieldPath } = useFieldPathResolver()
const { t } = useI18n()

const transformedFields = computed(() => {
  if (!props.fields?.items) return []

  const { evaluateVisibilityRules } = useVisibilityRules(companionGuestForm)

  return props.fields.items
    .filter((item) => {
      if (item.hidden) return false
      if (!item.visibility_rules) return true
      return evaluateVisibilityRules(item.visibility_rules)
    })
    .map((item) => ({
      ...item,
      render_type: item.type,
      // Use nested path for proper form data structure
      form_mapping: getFieldPath(item),
      label: item.field?.label || '',
      options: item.field?.options || [],
      core_id: item.field?.core_id || null,
      limits: item.field?.limits || null,
      display_availability: item.display_availability ?? false,
    }))
})

const generateCompanionGuestFields = (guest, fields) => {
  let initialData = {
    contact: {},
    extended_fields: {},
    booking: { extended_fields: {} },
  }

  if (fields) {
    fields.forEach((field) => {
      const path = getFieldPath(field)
      if (path) {
        const value = getNested(guest, path) ?? null
        dataSet(initialData, path, value)
      }
    })
  }

  return initialData
}

const route = window.route
const showCompanionTicketList = ref(true)
const companionGuestForm = ref({
  ticket: null,
  add_ons: [],
  add_ons_remove: [],
  ...generateCompanionGuestFields(props.model, props.fields.items),
})
const errors = ref({})
const tickets = ref([])
const addOns = ref([])
const eventId = inject('eventId')
const orderId = inject('orderId')
const orderType = inject('orderType')
const orderDetails = inject('orderDetails')
const activeLocale = inject('activeLocale', null)
const recalculateFieldLimits = inject('recalculateFieldLimits', null)
const registerCompanionForm = inject('registerCompanionForm', null)
const unregisterCompanionForm = inject('unregisterCompanionForm', null)
const fieldsUpdated = ref(false)

const backend = useCheckoutBackend({
  data: companionGuestForm,
  initialData: {
    ticket: null,
    add_ons: [],
    ...generateCompanionGuestFields(props.model, props.fields.items),
  },
  errors: errors,
  updateField: (path, value) => {
    dataSet(companionGuestForm.value, path, value)
  },
})

onMounted(() => {
  if (orderType.value === 'add-ons') {
    showCompanionTicketList.value = false
  }

  if (registerCompanionForm && props.model?.id) {
    registerCompanionForm(props.model.id, companionGuestForm)
  }

  fetchCompanionGuestTickets(props.model?.id)
  fetchCompanionGuestAddons(props.model?.id)
})

onBeforeUnmount(() => {
  if (unregisterCompanionForm && props.model?.id) {
    unregisterCompanionForm(props.model.id)
  }
})

watch(
  companionGuestForm,
  () => {
    if (recalculateFieldLimits) recalculateFieldLimits()
  },
  { deep: true },
)

const validate = () => {
  backend.clearErrors()

  const { evaluateVisibilityRules } = useVisibilityRules(companionGuestForm)
  const visibleFields = props.fields.items.filter((item) => {
    if (item.hidden) return false
    if (!item.visibility_rules) return true
    return evaluateVisibilityRules(item.visibility_rules)
  })

  // Only validate visible fields
  visibleFields.forEach((item) => {
    if (item.required) {
      const path = getFieldPath(item)
      const value = dataGet(companionGuestForm.value, path)
      if (
        [null, '', [], false].includes(value) ||
        (Array.isArray(value) && value.length === 0)
      ) {
        backend.addError(
          path,
          t('validation.required', { attribute: item.field.label }),
        )
      }
    }
  })

  const currentErrors = backend.getErrors()
  const isFormValid = Object.keys(currentErrors).length === 0

  return isFormValid
}

// Set validation errors from endpoint call
const setApiErrors = (apiErrors) => {
  backend.clearErrors()

  Object.keys(apiErrors).forEach((key) => {
    const messages = apiErrors[key]

    if (Array.isArray(messages)) {
      messages.forEach((message) => {
        backend.addError(key, message)
      })
    }
  })
}

// Fetch companion guest tickets
const fetchCompanionGuestTickets = async (guestId) => {
  if (!props.model?.id) return
  emit('is-loading', true)
  const { data, statusCode } = await getJson(
    route('api.checkout.guest.tickets.index', {
      event: eventId,
      order: orderId.value,
      guest: guestId,
    }),
  )
  if (statusCode.value && statusCode.value === 404) {
    emit('expired-session')
    return
  }
  tickets.value = data.value.data
  companionGuestForm.value.ticket = tickets.value?.find(
    ({ guest_has_ticket }) => guest_has_ticket,
  )?.id
}

// Fetch companion guest add-ons
const fetchCompanionGuestAddons = async (guestId) => {
  if (!props.model?.id) return
  emit('is-loading', true)
  const { data, statusCode } = await getJson(
    route('api.checkout.guest.addons.index', {
      event: eventId,
      order: orderId.value,
      guest: guestId,
    }),
  )
  if (statusCode.value && statusCode.value === 404) {
    emit('expired-session')
    return
  }
  addOns.value = data.value.data
  addOns.value.forEach((item) => {
    item.addons.forEach((addon) => {
      addon.items.map((lineItem) => {
        lineItem.details = orderDetails.value?.line_items.find(
          ({ option_from, option_to }) => {
            return (
              lineItem.starts_at === option_from &&
              lineItem.ends_at === option_to
            )
          },
        )
      })
    })
  })

  emit('is-loading', false)
}

const updateCompanionGuest = async (guest) => {
  // check validation
  if (!validate()) return false
  emit('is-loading', true)
  const { data, error, statusCode } = await useFetch(
    route('api.checkout.guest.update', {
      event: eventId,
      order: orderId.value,
      guest: props.model?.id,
      locale: activeLocale,
    }),
  ).patch(guest)
  if (statusCode.value && statusCode.value === 404) {
    emit('expired-session')
    return
  }
  if (error.value) {
    if (data.value?.errors) setApiErrors(data.value.errors)
    emit('is-loading', false)
    return
  }
  emit('success')
}
// Assign ticket to companion Guest
const assignTicketToCompanion = async (ticket_id) => {
  emit('is-loading', true)
  const { data, error, statusCode } = await putJson(
    route('api.checkout.guest.ticket.update', {
      event: eventId,
      order: orderId.value,
      guest: props.model?.id,
    }),
    { ticket_id: ticket_id },
  )
  if (statusCode.value && statusCode.value === 404) {
    emit('expired-session')
    return
  }
  if (error.value) {
    emit('error', data.value.message)
    emit('is-loading', false)
    return
  }

  await fetchCompanionGuestTickets(props.model?.id)
  await fetchCompanionGuestAddons(props.model?.id)
  emit('tickets-addons-success')
}
// Assign add-on to companion Guest
const assignAddOnToCompanion = async (addOn) => {
  emit('is-loading', true)
  const { data, error, statusCode } = await postJson(
    route('api.checkout.guest.addons.store', {
      event: eventId,
      order: orderId.value,
      guest: props.model?.id,
    }),
    {
      addon_id: addOn.id,
      start_at: addOn.start_at,
      end_at: addOn.end_at,
      quantity: addOn.quantity,
    },
  )
  if (statusCode.value && statusCode.value === 404) {
    emit('expired-session')
    return false
  }
  if (error.value) {
    emit('error', data.value?.message)
    emit('is-loading', false)
    return false
  }

  await fetchCompanionGuestAddons(props.model?.id)
  emit('tickets-addons-success')
  return true
}
// Remove add-on from companion Guest
const removeAddOnFromCompanion = async (addOn) => {
  emit('is-loading', true)
  const { statusCode } = await useFetch(
    route('api.checkout.guest.addons.destroy', {
      event: eventId,
      order: orderId.value,
      guest: props.model?.id,
    }),
  ).delete({
    reservation_id: addOn.reservation_id,
  })
  if (statusCode.value && statusCode.value === 404) {
    emit('expired-session')
    return
  }

  await fetchCompanionGuestAddons(props.model?.id)
  emit('tickets-addons-success')
}
</script>
